function EmbeddingSearch() {
  const [query, setQuery] = useState("");
  const [matchCount, setMatchCount] = useState(5);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    setStatus("");
    setResults([]);
    try {
      const res = await fetch("https://bylolucjhzsbbouoalah.supabase.co/functions/v1/search-embeddings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ query, matchCount }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResults(data.matches || []);
      setStatus(`Found ${data.matches?.length || 0} matches.`);
    } catch (err) {
      setStatus("Error: " + err.message);
    }
    setLoading(false);
  };

  return (
    <div className="my-8 p-4 border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
      <h2 className="text-lg font-bold mb-2">Semantic Search Test</h2>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          className="flex-1 border p-2 rounded text-gray-900 dark:text-white bg-white dark:bg-gray-900"
          placeholder="Enter search query..."
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <input
          type="number"
          min={1}
          max={20}
          className="w-20 border p-2 rounded text-gray-900 dark:text-white bg-white dark:bg-gray-900"
          value={matchCount}
          onChange={e => setMatchCount(Number(e.target.value))}
        />
        <button
          onClick={handleSearch}
          className="bg-blue-600 text-white px-4 py-2 rounded"
          disabled={loading}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </div>
      {status && <div className="mb-2 text-sm">{status}</div>}
      {results.length > 0 && (
        <ul className="space-y-2">
          {results.map((row, i) => (
            <li key={row.id} className="p-2 border rounded">
              <div className="font-bold">{row.vendor_name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-300">Similarity: {row.similarity?.toFixed(4)}</div>
              <div>{row.content}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
import React, { useState } from "react";
import VectorSearch from "../components/VectorSearch";
import DualSearchComparison from "../components/DualSearchComparison";
import VendorRagSearch from "../components/VendorRagSearch";
import { supabase } from "../services/supabase";

function VendorRagSearchTest() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [userIntent, setUserIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleResults = (searchResults) => {
    setResults(searchResults);
    setError(null);
  };

  const handleError = (err) => {
    setError(err.message);
    setResults([]);
  };

  const handleUserIntent = (intent) => {
    setUserIntent(intent);
  };

  return (
    <div className="my-8 p-4 border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
      <h2 className="text-lg font-bold mb-4">🚀 VendorRagSearch Test</h2>
      
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          className="flex-1 border p-2 rounded text-gray-900 dark:text-white bg-white dark:bg-gray-900"
          placeholder="Search for outdoor vendors... (e.g., 'horse riding', 'climbing gear')"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <VendorRagSearch
          query={query}
          onResults={handleResults}
          onLoading={setLoading}
          onError={handleError}
          onUserIntent={handleUserIntent}
        />
      </div>

      {loading && (
        <div className="text-blue-600 dark:text-blue-400 mb-2">
          🔍 Searching vendors...
        </div>
      )}

      {error && (
        <div className="text-red-600 dark:text-red-400 mb-2">
          ❌ Error: {error}
        </div>
      )}



      {results.length > 0 && (
        <div>
          <h3 className="font-bold mb-2">
            📋 Results ({results.length} found)
          </h3>
          <div className="space-y-3">
            {results.map((result, idx) => (
              <div key={result.ID || idx} className="p-3 border rounded bg-gray-50 dark:bg-gray-700">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-lg">{result.vendorName}</h4>
                  <div className="flex gap-1">
                    {result.sources.map(source => (
                      <span 
                        key={source}
                        className={`px-2 py-1 text-xs rounded ${
                          source === 'Vector' ? 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200' :
                          'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                        }`}
                      >
                        {source}
                      </span>
                    ))}
                    {result.llmRanked && (
                      <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200">
                        ✨ LLM
                      </span>
                    )}
                    {result.isOverlap && (
                      <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200">
                        OVERLAP
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <div><strong>Category:</strong> {result.metadata?.category || 'N/A'}</div>
                  <div><strong>State:</strong> {result.metadata?.state || 'N/A'}</div>
                  <div><strong>Score:</strong> {result.finalScore.toFixed(3)}</div>
                </div>
                
                {result.metadata?.content && (
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {result.metadata.content.substring(0, 200)}
                    {result.metadata.content.length > 200 && '...'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const AdminPage = () => {
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Supabase search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Call Supabase Edge Function to update embeddings
  const handleUpdateEmbeddings = async () => {
    setLoading(true);
    setStatus("");
    try {
      const res = await fetch("https://bylolucjhzsbbouoalah.supabase.co/functions/v1/update-vendor-embeddings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Optionally add Authorization if your function requires it:
          // "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ limit: 100 }),
      });
      const data = await res.json();
      setStatus(data.status || data.error || "Done.");
    } catch (err) {
      setStatus("Error: " + err.message);
    }
    setLoading(false);
  };

  // Example: Check embedding status
  const handleCheckStatus = async () => {
    setLoading(true);
    setStatus("");
    try {
      // Replace with your actual backend endpoint
      const res = await fetch("/api/embedding-status");
      if (!res.ok) throw new Error("Failed to fetch status");
      const data = await res.json();
      setStatus(data.status || "No status available.");
    } catch (err) {
      setStatus("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Supabase full-text search for vendor_chunks
  const runSearchQuery = async () => {
    if (!searchTerm.trim()) return;
    setSearchError(null);
    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from("vendor_chunks")
        .select("ID, vendor_name, content, category, state")
        .textSearch("content", searchTerm, { type: "websearch" })
        .limit(20);
      if (error) throw error;
      setSearchResults(data);
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-12 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Admin Controls</h1>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">(Uses OpenAI API key from <code>.env</code> as <code>REACT_APP_OPENAI_API_KEY</code>)</div>
      <div className="flex flex-col gap-4">
        <button
          onClick={handleUpdateEmbeddings}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-all duration-200"
          disabled={loading}
        >
          {loading ? "Updating Embeddings..." : "Update All Embeddings"}
        </button>

        <button
          onClick={handleCheckStatus}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium transition-all duration-200"
          disabled={loading}
        >
          Check Embedding Status
        </button>
        {status && (
          <div className="mt-4 p-3 border rounded bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100 whitespace-pre-line">
            {status}
          </div>
        )}
      </div>
      
      {/* Supabase Search Section */}
      <div className="my-8 p-4 border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
        <h2 className="text-lg font-bold mb-4">Supabase Text Search</h2>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            className="flex-1 border p-3 rounded text-gray-900 bg-white dark:text-gray-100 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && runSearchQuery()}
            placeholder="Enter search text..."
          />
          <button
            onClick={runSearchQuery}
            disabled={searchLoading || !searchTerm.trim()}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searchLoading ? "Searching..." : "Search Supabase"}
          </button>
        </div>
        
        {searchError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            Error: {searchError}
          </div>
        )}
        
        {searchResults.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800 dark:text-gray-200">
              Found {searchResults.length} results:
            </h3>
            {searchResults.map((row, idx) => (
              <div key={row.ID || idx} className="p-3 border rounded bg-gray-50 dark:bg-gray-900">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-blue-700 dark:text-blue-400">
                    {row.vendor_name || 'Unnamed Vendor'}
                  </h4>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ID: {row.ID}
                  </span>
                </div>
                {row.category && (
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                    <strong>Category:</strong> {row.category}
                  </div>
                )}
                {row.state && (
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                    <strong>State:</strong> {row.state}
                  </div>
                )}
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {row.content || 'No content available'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <VendorRagSearchTest />
      <EmbeddingSearch />
      <VectorSearch />
      <DualSearchComparison />
    </div>
  );
};

export default AdminPage;
